import { useEffect, useState } from "react";
import { X, Sparkles, ArrowRight, BookOpen, Check } from "lucide-react";
import { featuredProducts } from "@/data/featuredProducts";

const STORAGE_KEY = "bvn_guide_popup_dismissed_v1";

export default function ConversionPopup() {
    const [open, setOpen] = useState(false);
    const p = featuredProducts[0];

    useEffect(() => {
        if (!p) return;
        try {
            if (localStorage.getItem(STORAGE_KEY) === "1") return;
        } catch (e) { /* SSR / private mode — show anyway */ }

        let triggered = false;
        const trigger = () => {
            if (triggered) return;
            triggered = true;
            setOpen(true);
        };

        const t = window.setTimeout(trigger, 4500);
        const onScroll = () => {
            if (window.scrollY > 600) trigger();
        };
        window.addEventListener("scroll", onScroll, { passive: true });

        return () => {
            window.clearTimeout(t);
            window.removeEventListener("scroll", onScroll);
        };
    }, [p]);

    const close = () => {
        setOpen(false);
        try {
            localStorage.setItem(STORAGE_KEY, "1");
        } catch (e) { /* ignore */ }
    };

    const onCta = () => {
        try {
            localStorage.setItem(STORAGE_KEY, "1");
        } catch (e) { /* ignore */ }
    };

    if (!p || !open) return null;

    return (
        <div
            data-testid="conversion-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="conversion-popup-title"
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 animate-fade-in"
        >
            {/* Backdrop */}
            <button
                type="button"
                onClick={close}
                aria-label="Fermer"
                data-testid="conversion-popup-backdrop"
                className="absolute inset-0 bg-ink-950/60 backdrop-blur-sm"
            />

            {/* Card */}
            <div className="relative w-full max-w-2xl rounded-[2rem] overflow-hidden bg-white shadow-[0_40px_80px_-20px_rgba(15,23,42,0.5)] animate-fade-up">
                <button
                    onClick={close}
                    data-testid="conversion-popup-close"
                    aria-label="Fermer la fenêtre"
                    className="absolute top-4 right-4 z-10 grid place-items-center h-9 w-9 rounded-full bg-white/95 text-ink-700 hover:bg-ink-100 transition-colors shadow"
                >
                    <X className="h-4 w-4" strokeWidth={2.5} />
                </button>

                <div className="grid sm:grid-cols-[180px_1fr]">
                    {/* Cover */}
                    <div className="relative bg-ink-950 flex items-center justify-center p-5 sm:p-6">
                        <div className="absolute -top-2 -left-2 inline-flex items-center gap-1 rounded-full bg-pinky-500 text-white text-[9px] font-bold uppercase tracking-[0.16em] px-2.5 py-1.5 shadow-lg z-10">
                            <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
                            Offre lancement
                        </div>
                        <img
                            src={p.cover}
                            alt={p.name}
                            className="h-40 sm:h-56 w-auto object-contain rounded-lg shadow-[0_18px_40px_-12px_rgba(0,0,0,0.5)]"
                        />
                    </div>

                    {/* Content */}
                    <div className="p-6 sm:p-8 relative">
                        <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-600">
                            <BookOpen className="h-3.5 w-3.5" strokeWidth={2} />
                            {p.eyebrow}
                        </p>

                        <h2
                            id="conversion-popup-title"
                            className="font-display font-bold text-2xl sm:text-3xl text-ink-900 mt-3 leading-tight"
                        >
                            Boostez ses neurones <span className="text-brand-500">dès aujourd&apos;hui</span>.
                        </h2>

                        <p className="mt-3 text-sm sm:text-base text-ink-600 leading-relaxed">
                            {p.description}
                        </p>

                        <ul className="mt-4 space-y-1.5">
                            {p.benefits?.slice(0, 3).map((b) => (
                                <li key={b} className="flex items-start gap-2 text-sm text-ink-700">
                                    <Check className="h-4 w-4 text-brand-500 mt-0.5 flex-none" strokeWidth={2.5} />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-5 flex items-baseline gap-2.5">
                            <span className="font-display font-bold text-3xl text-ink-900">{p.price}</span>
                            {p.oldPrice && (
                                <span className="font-display text-lg text-ink-400 line-through decoration-pinky-500 decoration-2">
                                    {p.oldPrice}
                                </span>
                            )}
                            <span className="inline-flex items-center rounded-full bg-pinky-50 text-pinky-600 text-[10px] font-bold uppercase px-2 py-1">
                                {p.discountLabel || "Promo"}
                            </span>
                        </div>

                        <div className="mt-6 flex items-center gap-3">
                            <a
                                href={p.cta.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={onCta}
                                data-testid="conversion-popup-cta"
                                className="btn-fire group flex-1 sm:flex-none justify-center"
                            >
                                {p.cta.label}
                                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
                            </a>
                            <button
                                onClick={close}
                                data-testid="conversion-popup-dismiss"
                                className="text-xs font-medium text-ink-500 hover:text-ink-900 transition-colors"
                            >
                                Non merci
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
